import type { CSSProperties } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';

import { So5FixtureEvent } from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { Actions } from '@sorare/core/src/atoms/layout/Actions';
import { Horizontal, Vertical } from '@sorare/core/src/atoms/layout/flex';
import {
  BodyM,
  BrandM,
  LabelL,
  LabelM,
} from '@sorare/core/src/atoms/typography';
import { Dialog } from '@sorare/core/src/components/dialog';
import { ANY_SPORT_PLAY_EVENTTYPE } from '@sorare/core/src/constants/__generated__/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { tabletAndAbove } from '@sorare/core/src/style/mediaQuery';

import limited from './assets/limited.webp';
import rare from './assets/rare.webp';
import superRare from './assets/superRare.webp';
import unique from './assets/unique.webp';

const Root = styled(Vertical)`
  height: 100%;
  gap: var(--quadruple-unit);
  padding: 0 var(--double-unit);
`;
const Footer = styled(Actions).attrs({ direction: 'horizontal', gap: 1 })`
  padding: var(--double-unit);

  @media ${tabletAndAbove} {
    padding: var(--quadruple-unit) var(--double-unit) var(--double-unit);
  }
`;
const ScarcityGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  grid-template-rows: 1fr 1fr;
  flex: 1;
  gap: var(--double-unit);
  min-height: 0;

  @media ${tabletAndAbove} {
    min-height: 400px;
  }
`;
const Cell = styled(Vertical)`
  min-height: 0;
  min-width: 0;
`;
const ImgWrapper = styled(Horizontal).attrs({ center: true })`
  aspect-ratio: 1;
  border-radius: var(--double-unit);
  border: 1px solid var(--c-nd-50);
  background: var(--background) border-box;
  max-width: 100%;
  min-height: 0;
  height: 100%;
`;
const Img = styled.img`
  height: 80%;
`;

const scarcities = [
  {
    name: 'Limited',
    nb: 1000,
    img: limited,
    background: 'linear-gradient(180deg, #000 59.65%, #302000 100%)',
  },
  {
    name: 'Rare',
    nb: 100,
    img: rare,
    background: 'linear-gradient(180deg, #000 59.65%, #540000 100%)',
  },
  {
    name: 'Super Rare',
    nb: 10,
    img: superRare,
    background: 'linear-gradient(180deg, #000 59.65%, #002C54 100%)',
  },
  {
    name: 'Unique',
    nb: 1,
    img: unique,
    background: 'linear-gradient(180deg, #000 59.65%, #340054 100%)',
  },
];

type Props = { open: boolean; onClose: () => void };
export const NewNavDialog = ({ open, onClose }: Props) => {
  const { generateSportContextPath } = useSportContext();
  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="xs"
      fullWidth
      footer={
        <Footer>
          <Button
            size="large"
            color="tertiary"
            to={generateSportContextPath(ANY_SPORT_PLAY_EVENTTYPE, {
              params: {
                eventType: So5FixtureEvent.CLASSIC.toLowerCase(),
              },
            })}
          >
            <FormattedMessage defaultMessage="View Competitions" />
          </Button>
          <Button size="large" color="secondary" onClick={onClose}>
            <FormattedMessage defaultMessage="View Market" />
          </Button>
        </Footer>
      }
    >
      <Root className="text-center">
        <Vertical>
          <BrandM as="h2">
            <FormattedMessage defaultMessage="New Navigation" />
          </BrandM>
          <BodyM as="p">
            <FormattedMessage defaultMessage="You can view all the new unlocked Pro requirements per scarcity in each competition page." />
          </BodyM>
        </Vertical>
        <ScarcityGrid>
          {scarcities.map(({ name, nb, img, background }) => (
            <Cell key={name} gap={1.5} center>
              <ImgWrapper
                style={{ '--background': background } as CSSProperties}
              >
                <Img src={img} alt="" />
              </ImgWrapper>
              <Vertical gap={0} center>
                <LabelL>{name}</LabelL>
                <LabelM color="var(--c-nd-600)">
                  <FormattedMessage
                    defaultMessage="{nb, plural, one {{nb} Player Card} other {{nb} Player Cards}}"
                    values={{ nb }}
                  />
                </LabelM>
              </Vertical>
            </Cell>
          ))}
        </ScarcityGrid>
      </Root>
    </Dialog>
  );
};
